import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock } from "@/coquille-bridge";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { DataTable } from "@/components/DataTable.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMembershipCopy, useMembershipStore } from "@/membership/useMembership.js";
import {
  decideBdMember,
  inviteBdMember,
  updateMembershipSettings,
  appendHealthAudit,
} from "@/membership/membershipStore.js";
import { canViewHealthRecord } from "@/membership/healthPermissions.js";
import { membershipDisplayName, nextMissingStep } from "@/membership/membershipEngine.js";
import { formatFee } from "@/membership/membershipI18n.js";
import {
  BLACK_DRAGON_STATUSES,
  MEMBERSHIP_STATUSES,
  PAYMENT_STATUSES,
} from "@/membership/membershipConstants.js";
import { ADULT_HEALTH_FIELDS, MINOR_HEALTH_FIELDS } from "@/membership/healthFormCatalog.js";

const TABS = ["inProgress", "payments", "health", "active", "renewals", "bdRequests", "bdInvites"];

function filterTab(memberships, tab) {
  const today = new Date().toISOString().slice(0, 10);
  switch (tab) {
    case "payments":
      return memberships.filter((m) => m.payment?.status === PAYMENT_STATUSES.PENDING || m.status === MEMBERSHIP_STATUSES.PAYMENT_PENDING);
    case "health":
      return memberships.filter((m) => m.status === MEMBERSHIP_STATUSES.HEALTH_PENDING || nextMissingStep(m) === "health");
    case "active":
      return memberships.filter((m) => m.status === MEMBERSHIP_STATUSES.ACTIVE);
    case "renewals":
      return memberships.filter((m) => m.status === MEMBERSHIP_STATUSES.EXPIRED || (m.periodEnd && m.periodEnd <= today));
    case "bdRequests":
      return memberships.filter((m) => m.blackDragon?.status === BLACK_DRAGON_STATUSES.REQUEST_SUBMITTED);
    case "bdInvites":
      return memberships.filter((m) => m.blackDragon?.status === BLACK_DRAGON_STATUSES.INVITED);
    default:
      return memberships.filter((m) =>
        [MEMBERSHIP_STATUSES.DRAFT, MEMBERSHIP_STATUSES.FORM_INCOMPLETE, MEMBERSHIP_STATUSES.HEALTH_PENDING, MEMBERSHIP_STATUSES.PAYMENT_PENDING].includes(m.status),
      );
  }
}

export function AdminMembershipCockpitPage() {
  const { username, role } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const store = useMembershipStore();
  const [tab, setTab] = useState("inProgress");
  const [fee, setFee] = useState(String(store.settings.annualFeeCents / 100));
  const rows = useMemo(
    () =>
      filterTab(store.memberships, tab).map((m) => ({
        id: m.id,
        who: membershipDisplayName(m),
        kind: m.kind === "minor" ? copy.admin.minor : copy.admin.adult,
        missing: nextMissingStep(m),
        status: copy.statuses[m.status] || m.status,
        dojo: m.identity?.dojoId || "—",
        email: m.kind === "minor" ? m.parent?.email : m.identity?.email,
        bd: copy.bdStatuses[m.blackDragon?.status] || "",
        _raw: m,
      })),
    [store.memberships, tab, copy],
  );

  return (
    <AdminGuard>
      <PageSection title={copy.admin.title} subtitle={copy.admin.subtitle} testId="page-admin-adhesions">
        <div className="membership-page" data-surface="light">
          <label>
            {copy.admin.fee}
            <input data-testid="admin-fee-input" value={fee} onChange={(e) => setFee(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn-secondary"
            data-testid="admin-fee-save"
            onClick={() => updateMembershipSettings({ annualFeeCents: Math.round(Number(fee) * 100) || store.settings.annualFeeCents })}
          >
            {copy.admin.saveFee}
          </button>
          <p className="membership-fee" data-testid="admin-fee-display">
            {formatFee(store.settings.annualFeeCents, store.settings.currency, locale)}
          </p>
          <div className="membership-tabs" data-testid="adhesions-tabs">
            {TABS.map((t) => (
              <button key={t} type="button" className={tab === t ? "is-active" : ""} data-testid={`adhesions-tab-${t}`} onClick={() => setTab(t)}>
                {copy.admin.tabs[t]}
              </button>
            ))}
          </div>
          <DataTable
            rows={rows}
            columns={[
              { key: "who", label: copy.admin.columns.who, sortable: true, render: (r) => <Link to={`/admin/adhesions/${r.id}`}>{r.who}</Link> },
              { key: "kind", label: copy.admin.columns.kind, sortable: true },
              { key: "missing", label: copy.admin.columns.missing, sortable: true },
              { key: "status", label: copy.admin.columns.status, sortable: true },
              { key: "email", label: copy.admin.columns.email, sortable: true },
              {
                key: "actions",
                label: copy.admin.open360,
                render: (r) => (
                  <span>
                    <Link to={`/admin/adhesions/${r.id}`} data-testid={`adhesion-360-${r.id}`}>
                      {copy.admin.open360}
                    </Link>
                    {r._raw.status === MEMBERSHIP_STATUSES.ACTIVE && r._raw.blackDragon?.status === BLACK_DRAGON_STATUSES.NOT_MEMBER ? (
                      <button type="button" data-testid={`adhesion-invite-bd-${r.id}`} onClick={() => inviteBdMember(r.id, { username, role })}>
                        {copy.admin.inviteBd}
                      </button>
                    ) : null}
                    {r._raw.blackDragon?.status === BLACK_DRAGON_STATUSES.REQUEST_SUBMITTED || r._raw.blackDragon?.status === BLACK_DRAGON_STATUSES.INVITED ? (
                      <>
                        <button type="button" data-testid={`adhesion-approve-bd-${r.id}`} onClick={() => decideBdMember(r.id, "approve", { username, role })}>
                          {copy.admin.approveBd}
                        </button>
                        <button type="button" data-testid={`adhesion-refuse-bd-${r.id}`} onClick={() => decideBdMember(r.id, "refuse", { username, role })}>
                          {copy.admin.refuseBd}
                        </button>
                      </>
                    ) : null}
                  </span>
                ),
              },
            ]}
            searchFields={["who", "email", "status", "kind"]}
            filters={[
              {
                field: "kind",
                label: copy.admin.columns.kind,
                options: [
                  { value: copy.admin.adult, label: copy.admin.adult },
                  { value: copy.admin.minor, label: copy.admin.minor },
                ],
              },
            ]}
            emptyMessage={copy.admin.empty}
            emptyFilteredMessage={copy.admin.emptyFiltered}
            testId="adhesions-table"
          />
        </div>
      </PageSection>
    </AdminGuard>
  );
}

export function AdminMember360Page() {
  const { id } = useParams();
  const { username, role } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const store = useMembershipStore();
  const record = store.memberships.find((m) => m.id === id);
  const viewer = { role, username, membershipId: null, linkedMembershipIds: [] };
  const canHealth = canViewHealthRecord(viewer, record, store.settings);
  const fields = record?.kind === "minor" ? MINOR_HEALTH_FIELDS : ADULT_HEALTH_FIELDS;

  useEffect(() => {
    if (canHealth && record?.id) {
      appendHealthAudit({ actorUsername: username, actorRole: role, membershipId: record.id, action: "view_admin" });
    }
  }, [canHealth, record?.id, username, role]);

  return (
    <AdminGuard>
      <PageSection title={copy.admin.member360Title} testId="page-admin-member-360">
        {!record ? (
          <p>{copy.admin.empty}</p>
        ) : (
          <GsdReadableBlock testId="member-360-panel">
            <p data-testid="member-360-name">
              {record.identity.firstName} {record.identity.lastName} — {copy.statuses[record.status]}
            </p>
            <h3>{copy.admin.sectionIdentity}</h3>
            <p>
              {copy.fields.firstName} : {record.identity.firstName || copy.admin.toComplete}
            </p>
            <p>
              {copy.fields.lastName} : {record.identity.lastName || copy.admin.toComplete}
            </p>
            <p data-testid="member-360-birth">
              {copy.fields.birthDate} : {record.identity.birthDate || copy.admin.toComplete}
            </p>
            <h3>{copy.admin.sectionContact}</h3>
            <p data-testid="member-360-phone">
              {copy.fields.phone} : {record.identity.phone || copy.admin.toComplete}
            </p>
            <p data-testid="member-360-email">
              {copy.fields.email} : {record.identity.email || copy.admin.toComplete}
              {record.reviewFlags?.includes("email-suspect") ? ` — ${copy.admin.emailToVerify}` : ""}
            </p>
            <p>
              {copy.fields.address} : {record.identity.address || copy.admin.toComplete}
            </p>
            <h3>{copy.admin.sectionDojo}</h3>
            <p>
              {copy.fields.dojo} : {record.identity.dojoId || copy.admin.toComplete}
            </p>
            <h3>{copy.admin.sectionInstructor}</h3>
            <p data-testid="member-360-instructor">
              {copy.fields.instructor} : {record.identity.instructorId || copy.admin.toComplete}
            </p>
            <h3>{copy.admin.sectionGrade}</h3>
            <p data-testid="member-360-grade">{record.source?.original?.lastBelt || copy.admin.toComplete}</p>
            <h3>{copy.admin.sectionParent}</h3>
            {record.parent ? (
              <p data-testid="member-360-parent">
                {record.parent.firstName} {record.parent.lastName} ({record.parent.email || copy.admin.toComplete})
              </p>
            ) : (
              <p data-testid="member-360-parent">{copy.admin.toComplete}</p>
            )}
            <h3>{copy.admin.sectionMembership}</h3>
            <p>
              {copy.admin.columns.kind} : {record.kind === "minor" ? copy.admin.minor : copy.admin.adult}
            </p>
            <p>
              {copy.portal.amount} : {formatFee(record.payment?.amountCents, record.payment?.currency, locale)} · {record.payment?.transactionId || "—"}
            </p>
            <p data-testid="member-360-bd">
              {copy.portal.bdTitle} : {copy.bdStatuses[record.blackDragon?.status] || "—"}
            </p>
            <p data-testid="member-360-no-admin-rights">
              {copy.admin.roles} : {record.adminPermissionsGranted ? "admin" : "member"}
            </p>
            <p data-testid="member-360-no-login">{record.noLogin ? copy.officialImport.noLogin : ""}</p>
            <h3>{copy.admin.sectionHealth}</h3>
            {canHealth ? (
              <dl data-testid="member-360-health">
                {fields.map((f) => (
                  <React.Fragment key={f.id}>
                    <dt>{copy.health[f.id]}</dt>
                    <dd>{String(record.health?.answers?.[f.id] ?? copy.admin.toComplete)}</dd>
                  </React.Fragment>
                ))}
                {record.health?.answers?.ckaMedicalSelections ? (
                  <>
                    <dt>CKA</dt>
                    <dd>{record.health.answers.ckaMedicalSelections}</dd>
                  </>
                ) : null}
              </dl>
            ) : (
              <p className="membership-restricted" data-testid="member-360-health-hidden">
                {copy.portal.healthHidden}
              </p>
            )}
            <h3>{copy.admin.sectionHistory}</h3>
            <p data-testid="member-360-source">
              {copy.officialImport.source} : {record.source?.name || record.origin || "—"} #{record.source?.rowId || "—"}
            </p>
            {record.reviewFlags?.length ? <p data-testid="member-360-review">{copy.admin.toVerify}: {record.reviewFlags.join(", ")}</p> : null}
            <p>
              <Link to="/admin/adhesions">{copy.admin.title}</Link>
            </p>
          </GsdReadableBlock>
        )}
      </PageSection>
    </AdminGuard>
  );
}

export function AdminBlackDragonCockpitPage() {
  const { username, role } = useAuth();
  const { copy } = useMembershipCopy();
  const store = useMembershipStore();
  const rows = store.memberships
    .filter((m) =>
      [BLACK_DRAGON_STATUSES.REQUEST_SUBMITTED, BLACK_DRAGON_STATUSES.INVITED, BLACK_DRAGON_STATUSES.ACTIVE, BLACK_DRAGON_STATUSES.PENDING_APPROVAL].includes(
        m.blackDragon?.status,
      ),
    )
    .map((m) => ({
      id: m.id,
      who: membershipDisplayName(m),
      status: copy.bdStatuses[m.blackDragon?.status],
      membership: copy.statuses[m.status],
      _raw: m,
    }));
  return (
    <AdminGuard>
      <PageSection title={copy.admin.tabs.bdRequests} subtitle={copy.portal.bdInviteHint} testId="page-admin-black-dragon-adhesions">
        <DataTable
          rows={rows}
          columns={[
            { key: "who", label: copy.admin.columns.who, sortable: true },
            { key: "status", label: copy.admin.columns.status, sortable: true },
            { key: "membership", label: copy.portal.adhesionTitle, sortable: true },
            {
              key: "actions",
              label: copy.admin.approveBd,
              render: (r) => (
                <span>
                  <button type="button" data-testid={`bd-cockpit-approve-${r.id}`} onClick={() => decideBdMember(r.id, "approve", { username, role })}>
                    {copy.admin.approveBd}
                  </button>
                  <button type="button" data-testid={`bd-cockpit-refuse-${r.id}`} onClick={() => decideBdMember(r.id, "refuse", { username, role })}>
                    {copy.admin.refuseBd}
                  </button>
                </span>
              ),
            },
          ]}
          searchFields={["who", "status"]}
          emptyMessage={copy.admin.empty}
          emptyFilteredMessage={copy.admin.emptyFiltered}
          testId="bd-adhesions-table"
        />
      </PageSection>
    </AdminGuard>
  );
}
